import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {IDispatchJob, IDispatchPoint, IDispatchRoute} from "../../../models/IDispatch";
import {
    fetchAddDispatchJob,
    fetchAddDispatchPoint,
    fetchAddDispatchRoute,
    fetchDeleteDispatchJob,
    fetchDeleteDispatchRoute,
    fetchDispatchDay,
    fetchGetDispatchPoints,
    fetchSetDispatchPointArchived,
    fetchUpdateDispatchJob,
    fetchUpdateDispatchPoint,
    fetchUpdateDispatchRoute,
} from "./actions";

/**
 * Состояние диспетчеризации.
 *
 * jobs/routes — срез ОДНОГО дня (selectedDate): доска работает по дням,
 * держать в сторе больше одного дня незачем. Мутационные thunks после
 * успеха сами перечитывают день (fetchDispatchDay) — точечный мерж
 * не нужен, объёмы маленькие (десятки строк).
 *
 * Валидаторы карточки в стор не кладутся — считаются на лету и живут
 * локально в диалоге (устаревают при догрузке архива).
 */
interface IDispatchingState {
    /** День доски, YYYY-MM-DD (день компании) */
    selectedDate: string;
    points: IDispatchPoint[];
    jobs: IDispatchJob[];
    routes: IDispatchRoute[];
    isLoading: boolean;
    /** Тихая фоновая перечитка (поллинг) — без глобального спиннера */
    isRefreshing: boolean;
    errorMessage: string;
}

const initialState: IDispatchingState = {
    selectedDate: dayjs().format("YYYY-MM-DD"),
    points: [],
    jobs: [],
    routes: [],
    isLoading: false,
    isRefreshing: false,
    errorMessage: "",
};

const upsertPoint = (list: IDispatchPoint[], point: IDispatchPoint) => {
    const idx = list.findIndex((p) => p.id === point.id);
    if (idx === -1) list.push(point);
    else list[idx] = point;
};

export const DispatchingSlice = createSlice({
    name: "dispatching",
    initialState,
    reducers: {
        setDispatchDate: (state, action: PayloadAction<string>) => {
            state.selectedDate = action.payload;
            // Смена дня — старые списки не показываем до перечитки.
            state.jobs = [];
            state.routes = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // ── День ────────────────────────────────────────────────────────
            .addCase(fetchDispatchDay.pending, (state, action) => {
                // Первый заход — полноценный лоадер; поллинг — тихий.
                if (state.jobs.length || state.routes.length) {
                    state.isRefreshing = true;
                } else {
                    state.isLoading = true;
                }
                state.errorMessage = "";
                void action;
            })
            .addCase(fetchDispatchDay.fulfilled, (state, action) => {
                state.jobs = action.payload.jobs;
                state.routes = action.payload.routes;
                state.isLoading = false;
                state.isRefreshing = false;
            })
            .addCase(fetchDispatchDay.rejected, (state, action) => {
                state.isLoading = false;
                state.isRefreshing = false;
                state.errorMessage = (action.payload as string) ?? "";
            })
            // ── Справочник точек ───────────────────────────────────────────
            .addCase(fetchGetDispatchPoints.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchGetDispatchPoints.fulfilled, (state, action) => {
                state.points = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchGetDispatchPoints.rejected, (state, action) => {
                state.isLoading = false;
                state.errorMessage = (action.payload as string) ?? "";
            })
            .addCase(fetchAddDispatchPoint.fulfilled, (state, action) => {
                upsertPoint(state.points, action.payload);
            })
            .addCase(fetchUpdateDispatchPoint.fulfilled, (state, action) => {
                upsertPoint(state.points, action.payload);
            })
            .addCase(fetchSetDispatchPointArchived.fulfilled, (state, action) => {
                upsertPoint(state.points, action.payload);
            })
            // ── Точечные fulfilled без перечитки дня ───────────────────────
            // add/update job и update route перечитывают день сами (в thunk),
            // но кладём результат сразу — интерфейс отзывчивее на медленной сети.
            .addCase(fetchAddDispatchJob.fulfilled, (state, action) => {
                if (action.payload.date === state.selectedDate) {
                    state.jobs.push(action.payload);
                }
            })
            .addCase(fetchUpdateDispatchJob.fulfilled, (state, action) => {
                const idx = state.jobs.findIndex((j) => j.id === action.payload.id);
                if (idx !== -1) state.jobs[idx] = action.payload;
            })
            .addCase(fetchDeleteDispatchJob.fulfilled, (state, action) => {
                state.jobs = state.jobs.filter((j) => j.id !== action.payload);
            })
            .addCase(fetchAddDispatchRoute.fulfilled, (state, action) => {
                if (action.payload.date === state.selectedDate) {
                    state.routes.push(action.payload);
                }
            })
            .addCase(fetchUpdateDispatchRoute.fulfilled, (state, action) => {
                const idx = state.routes.findIndex((r) => r.id === action.payload.id);
                if (idx !== -1) state.routes[idx] = action.payload;
            })
            .addCase(fetchDeleteDispatchRoute.fulfilled, (state, action) => {
                state.routes = state.routes.filter((r) => r.id !== action.payload);
            });
    },
});

export const {setDispatchDate} = DispatchingSlice.actions;
export default DispatchingSlice.reducer;
